import React, { useMemo, useState } from "react";
import { Badge, Button, Input, MessageBar, MessageBarBody, Text } from "@fluentui/react-components";
import { Add20Regular, Dismiss20Regular } from "@fluentui/react-icons";
import { addInvitees, removeInvitee } from "../spurt/meetings";
import type { Invitee, MeetingView, Rsvp } from "../spurt/meetings";
import type { DirUser } from "../spurt/types";
import { errText } from "./meetingErrors";
import css from "./Meetings.module.css";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const RSVP_TEXT: Record<Rsvp, string> = { none: "No reply", accepted: "Accepted", tentative: "Maybe", declined: "Declined" };
const RSVP_COLOR: Record<Rsvp, "success" | "warning" | "danger" | "subtle"> = { accepted: "success", tentative: "warning", declined: "danger", none: "subtle" };

export const inviteeLabel = (i: Invitee, users: Record<string, DirUser>): string => {
  if (i.type === "user") return (i.userId && users[i.userId]?.name) || i.name || i.userId || "Unknown person";
  if (i.type === "guest") return i.name || i.email || "Guest";
  if (i.type === "team") return i.name || "A team";
  return i.name || "A conversation";
};

export const inviteeKey = (i: Invitee): string => {
  if (i.type === "user" && i.userId) return `u:${i.userId}`;
  if (i.type === "guest" && i.email) return `e:${i.email}`;
  if (i.type === "team" && i.groupId) return `t:${i.groupId}`;
  if (i.type === "conversation" && i.groupId) return `c:${i.groupId}`;
  return i.userId ?? i.email ?? i.groupId ?? "";
};

export interface InviteesSectionProps {
  meeting: MeetingView;
  host: boolean;
  users: Record<string, DirUser>;
  onUpdated: (m: MeetingView) => void;
}

const InviteesSection: React.FC<InviteesSectionProps> = ({ meeting, host, users, onUpdated }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const invitees = meeting.invitees ?? [];
  const open = meeting.status !== "ended" && meeting.status !== "canceled";

  const invited = useMemo(() => new Set(invitees.map((i) => i.userId).filter(Boolean)), [invitees]);
  const q = text.trim().toLowerCase();
  const suggestions = useMemo(
    () =>
      q.length < 1
        ? []
        : Object.values(users)
            .filter((u) => !invited.has(u.id) && (u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)))
            .slice(0, 6),
    [q, users, invited],
  );

  const run = async (fn: () => Promise<MeetingView>, what: string) => {
    setBusy(true);
    setError("");
    try {
      onUpdated(await fn());
      setText("");
    } catch (e) {
      setError(errText(e, what));
    } finally {
      setBusy(false);
    }
  };

  const addEmail = () => {
    const email = text.trim().toLowerCase();
    if (!EMAIL.test(email)) {
      setError("Type a full email address, or pick someone from the list.");
      return;
    }
    void run(() => addInvitees(meeting.id, [{ email }]), "add people");
  };

  return (
    <section className={css.section} aria-label="Invitees">
      <h3 className={css.h3}>People ({invitees.length})</h3>
      {invitees.length === 0 && <Text className={css.muted}>Nobody else is invited.</Text>}
      <ul className={css.plain}>
        {invitees.map((i, n) => {
          const label = inviteeLabel(i, users);
          const key = inviteeKey(i);
          return (
            <li key={key || n} className={css.invitee}>
              <div className={css.inviteeName}>
                <span>{label}</span>
                {(i.type === "guest" || i.type === "team" || i.type === "conversation") && <span className={css.muted}> · {i.type === "guest" ? "external guest" : i.type}</span>}
                {i.optional && <span className={css.muted}> · optional</span>}
              </div>
              {(host || (i.rsvp && i.rsvp !== "none")) && (
                <Badge appearance="tint" color={RSVP_COLOR[i.rsvp ?? "none"]}>
                  {RSVP_TEXT[i.rsvp ?? "none"]}
                </Badge>
              )}
              {host && open && key && (
                <Button size="small" appearance="subtle" icon={<Dismiss20Regular />} aria-label={`Remove ${label}`} disabled={busy} onClick={() => void run(() => removeInvitee(meeting.id, key), "remove this person")} />
              )}
            </li>
          );
        })}
      </ul>

      {host && open && (
        <div className={css.stack}>
          <div className={css.row}>
            <Input
              className={css.grow}
              value={text}
              placeholder="Add people: a name or an email address"
              aria-label="Add people"
              onChange={(_, d) => {
                setText(d.value);
                setError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (suggestions.length === 1 && suggestions[0]) void run(() => addInvitees(meeting.id, [{ userId: suggestions[0]!.id }]), "add people");
                  else if (text.trim()) addEmail();
                }
              }}
            />
            <Button icon={<Add20Regular />} disabled={busy || !text.trim()} onClick={addEmail}>
              Add email
            </Button>
          </div>
          {suggestions.length > 0 && (
            <ul className={css.plain} aria-label="People you can add">
              {suggestions.map((u) => (
                <li key={u.id}>
                  <Button appearance="subtle" disabled={busy} onClick={() => void run(() => addInvitees(meeting.id, [{ userId: u.id }]), "add people")}>
                    {u.name} <span className={css.muted}>&nbsp;{u.email}</span>
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {error && (
        <MessageBar intent="error" role="alert">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
    </section>
  );
};

export default InviteesSection;
